import { useEffect, useId, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Application } from "@/lib/api/envoy";
import type { PostingFit } from "@/lib/api/resumeService";
import { fitOf } from "@/lib/applications";
import { actionProps } from "@/lib/controlAnalytics";
import { useApplicationFit } from "@/lib/hooks/envoy";

/** Explain the exact score shown, using its saved match or its current resume fit. */
export default function KeywordMatch({
    application,
    fit,
    children,
    className = "font-mono text-[13px] font-semibold text-brand-ink",
}: {
    application?: Application;
    fit?: PostingFit;
    children?: ReactNode;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<CSSProperties>({});
    const trigger = useRef<HTMLButtonElement>(null);
    const panel = useRef<HTMLDivElement>(null);
    const closing = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const id = useId();
    const saved = useApplicationFit(open && application ? application.application_id : null);
    const asked = application?.keywords_asked ?? fit?.asked ?? 0;
    const covered = Math.min(application?.keywords_covered ?? fit?.covered ?? 0, asked);
    const coveredTerms = application ? saved.data?.covered : fit?.covered_keywords;
    const missingTerms = application
        ? saved.data?.missing
        : fit?.missing.map((keyword) => keyword.term);
    const keep = () => clearTimeout(closing.current);
    const show = () => {
        keep();
        const rect = trigger.current?.getBoundingClientRect();
        if (!rect) return;
        const width = Math.min(360, window.innerWidth - 24);
        const below = window.innerHeight - rect.bottom - 16;
        const above = rect.top - 16;
        const upward = below < 260 && above > below;
        setPosition({
            position: "fixed",
            width,
            left: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
            ...(upward ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }),
            maxHeight: Math.max(100, Math.min(420, upward ? above : below)),
        });
        setOpen(true);
    };
    const leave = () => {
        closing.current = setTimeout(() => setOpen(false), 150);
    };
    useEffect(() => () => clearTimeout(closing.current), []);
    useEffect(() => {
        if (!open) return;
        const outside = (event: PointerEvent) => {
            if (
                !trigger.current?.contains(event.target as Node) &&
                !panel.current?.contains(event.target as Node)
            )
                setOpen(false);
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };
        // A resize's target is the window, which is no node and never inside.
        const move = (event: Event) => {
            if (!(event.target instanceof Node) || !panel.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener("pointerdown", outside);
        document.addEventListener("keydown", escape);
        window.addEventListener("resize", move);
        window.addEventListener("scroll", move, true);
        return () => {
            document.removeEventListener("pointerdown", outside);
            document.removeEventListener("keydown", escape);
            window.removeEventListener("resize", move);
            window.removeEventListener("scroll", move, true);
        };
    }, [open]);
    const words = (terms: readonly string[], matched: boolean) => (
        <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
            {terms.map((term, index) => (
                <li
                    key={`${term}:${index}`}
                    className={`rounded-md px-2 py-1 text-[12px] ${matched ? "bg-applied text-applied-fg" : "border border-line bg-card-raised text-fg-muted"}`}
                >
                    {term}
                </li>
            ))}
        </ul>
    );
    return (
        <>
            <button
                ref={trigger}
                {...actionProps("keyword_match_details_opened", {
                    source: "KeywordMatch",
                    application_id: application?.application_id,
                    posting_id: application?.posting_id ?? fit?.posting_id,
                })}
                type="button"
                aria-label={`Show keywords: ${covered} of ${asked} matched`}
                aria-expanded={open}
                aria-controls={open ? id : undefined}
                aria-describedby={open ? id : undefined}
                onMouseEnter={show}
                onMouseLeave={leave}
                onFocus={show}
                onBlur={(event) => {
                    if (!panel.current?.contains(event.relatedTarget as Node | null))
                        setOpen(false);
                }}
                onClick={(event) => {
                    event.stopPropagation();
                    show();
                }}
                className={`cursor-help whitespace-nowrap rounded text-left underline decoration-dotted decoration-current/40 underline-offset-4 ${className}`}
            >
                {children ??
                    (application ? fitOf(application).label : `${covered} of ${asked} keywords`)}
            </button>
            {open &&
                createPortal(
                    <div
                        ref={panel}
                        id={id}
                        role="tooltip"
                        data-keyword-details="true"
                        tabIndex={0}
                        onMouseEnter={keep}
                        onMouseLeave={leave}
                        onFocus={keep}
                        onBlur={(event) => {
                            if (!trigger.current?.contains(event.relatedTarget as Node | null))
                                setOpen(false);
                        }}
                        style={position}
                        className="z-[70] flex flex-col gap-3 overflow-y-auto rounded-card border border-line bg-card p-4 text-fg shadow-pop"
                    >
                        <div>
                            <p className="m-0 text-[14px] font-semibold">
                                {covered} of {asked} keywords matched
                            </p>
                            <p className="mb-0 mt-1 text-[11.5px] text-fg-muted">
                                {application
                                    ? "Against your baseline when this job matched."
                                    : "Against your saved resume as it stands."}
                            </p>
                        </div>
                        {application && saved.isPending ? (
                            <p role="status" className="m-0 text-[12px] text-fg-muted">
                                Loading keyword details…
                            </p>
                        ) : application && saved.isError ? (
                            <p role="status" className="m-0 text-[12px] text-fg-muted">
                                Keyword details could not be loaded. Close this and try again.
                            </p>
                        ) : coveredTerms == null || missingTerms == null ? (
                            <p className="m-0 text-[12px] text-fg-muted">
                                The keyword lists were not saved for this older match. Only its
                                counts are available.
                            </p>
                        ) : (
                            <>
                                <section>
                                    <h3 className="mb-2 mt-0 text-[12px] font-semibold">
                                        Matched ({coveredTerms.length})
                                    </h3>
                                    {coveredTerms.length ? (
                                        words(coveredTerms, true)
                                    ) : (
                                        <p className="m-0 text-[12px] text-fg-muted">None.</p>
                                    )}
                                </section>
                                <section>
                                    <h3 className="mb-2 mt-0 text-[12px] font-semibold">
                                        Not matched ({missingTerms.length})
                                    </h3>
                                    {missingTerms.length ? (
                                        words(missingTerms, false)
                                    ) : (
                                        <p className="m-0 text-[12px] text-fg-muted">
                                            None. Every requested keyword is covered.
                                        </p>
                                    )}
                                </section>
                            </>
                        )}
                    </div>,
                    document.body,
                )}
        </>
    );
}
